import React, { Fragment, useContext, useEffect, useState } from "react";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ErrorIcon from '@mui/icons-material/Error';
import SaveIcon from '@mui/icons-material/Save';
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { APIContext } from "../../Contexts/APIContext";
import { ScorecardContext } from "../../Contexts/ScorecardContext";
import { SaveAnswerViewModel } from "../ViewModels/SaveAnswerViewModel";
import { AnswerLookupViewModel, MonitoringQuestionViewModel } from "../ViewModels/GetMonitoringScorecardResponse";
import QuestionSavingStatusViewModel from "../ViewModels/QuestionSavingStatusViewModel";

interface IProps {
    callRefId: string
    question: MonitoringQuestionViewModel;
    readOnly: boolean;
    grey: boolean;
    updateParent: () => void;
    answers: AnswerLookupViewModel[];
    completeId: number;
    feedbackView?: string;
}

export default function MonitoringQuestion(props: IProps) {
    const { post } = useContext(APIContext);
    const { updateSavedStatus, questionsSavedStatus } = useContext(ScorecardContext);
    const [value, setValue] = useState<string>(props.question.value);
    const [savedValue, setSavedValue] = useState<string>(props.question.value);

    useEffect(() => {
        updateQuestionSavedStatus(false);
    }, [savedValue, value])

    const updateQuestionSavedStatus = (isSaving: boolean, newValue?: string) => {
        const question: QuestionSavingStatusViewModel =
        {
            id: props.question.id,
            isSaving: isSaving,
            isSaved: value === (newValue ?? savedValue)
        };
        updateSavedStatus(question);
    }

    const handleSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateQuestionSavedStatus(false, event.target.value);
        saveField(event.target.value);
    }

    const handleRichTextChange = (content: string) => {
        setValue(content);
    }

    const blurText = () => {
        saveField();
    }

    const saveField = (newValue?: string) => {
        if (!props.readOnly) {
            const viewModel = new SaveAnswerViewModel(
                props.callRefId,
                props.completeId,
                props.question.id,
                newValue ?? value);

            let controllerAction = 'SaveAnswer';

            if (props.feedbackView === "Team Leader Feedback View") {
                controllerAction += `?view=${props.feedbackView}`;
            }

            updateQuestionSavedStatus(true)

            post('MonitoringScorecard',
                controllerAction,
                (viewModel),
                () => {
                    if (newValue != undefined) {
                        setValue(newValue);
                    }

                    updateQuestionSavedStatus(false)
                    setSavedValue(newValue ?? value);
                    props.updateParent()
                },
                () => {
                    updateQuestionSavedStatus(false)
                },
            );
        }
    }

    const isSaving = questionsSavedStatus.find(question => question.id === props.question.id)?.isSaving;
    const isDisabled = props.readOnly || isSaving;

    return (
        <Grid
            container
            justifyContent="space-between"
            style={props.grey && props.question.type !== "RichText" ? { backgroundColor: "#8080801f" } : {}} >
            {
                props.question.type === "Subheading" ?
                    <Grid size={12}>
                        <Typography
                            textAlign="left"
                            variant="body1">
                            <u>{props.question.text}</u>
                        </Typography>
                    </Grid>
                    :
                    <Fragment>
                        <Grid size={props.question.type === "Dropdown" ? 9 : 12}>
                            {props.question.type === "RichText" ?
                                <Typography variant="body1"><u>{props.question.text}</u></Typography> :
                                <Typography variant="body1" style={{ paddingLeft: '5px' }}>{props.question.text}</Typography>
                            }
                            {props.question.description &&
                                <Typography variant="body2"><i>{props.question.description}</i></Typography>
                            }
                        </Grid>
                        {props.question.type === "TextDropdown" && <Grid size={6} />}
                        {props.question.type === "RichText" ?
                            <Grid size={12}>
                                <ReactQuill
                                    theme="snow"
                                    value={value}
                                    onChange={handleRichTextChange}
                                    onBlur={isDisabled ? () => { } : blurText}
                                    modules={{ toolbar: false }}
                                    readOnly={props.readOnly}
                                />
                                <Grid container justifyContent="flex-end" size={12}>
                                    <Button onClick={() => saveField()} disabled={value === savedValue}>
                                        <Typography
                                            color={value === savedValue ? 'Green' : 'Red'}
                                            paddingRight={1}>
                                            {
                                                value === savedValue ?
                                                    "Saved"
                                                    : "Not Saved!"
                                            }
                                        </Typography>
                                        {
                                            isSaving ?
                                                <CircularProgress size={20} />
                                                :
                                                <SaveIcon />
                                        }
                                    </Button>
                                </Grid>
                            </Grid>
                            :
                            <Grid container size={props.question.type === "TextDropdown" ? 6 : 3}>
                                <Grid size={2}>
                                    {isSaving && <CircularProgress size={20} sx={{ marginTop: "4px" }} />}
                                </Grid>
                                <Grid size={10} style={{ alignSelf: 'end' }}>
                                    <FormControl variant="standard" fullWidth>
                                        <Select
                                            size="small"
                                            fullWidth
                                            value={value}
                                            onChange={handleSelectChange}
                                            disabled={isDisabled}
                                        >
                                            {props.question.dropdown.map((item, index) =>
                                                <MenuItem key={index} value={index}>
                                                    {props.question.type !== "TextDropdown" ? `${item.label} (${item.value}) ` : `${item.label}`}
                                                    {item.type === "Fail" ? <ErrorIcon sx={{ verticalAlign: "middle" }} fontSize="medium" color="error" /> : ""}
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        }
                        {props.question.dependentQuestions.length > 0 &&
                            props.question.dependentQuestions.map(q =>
                                q.dependentValue === props.question.dropdown[value]?.value.toString() ?
                                    <Grid size={12}>
                                        <MonitoringQuestion
                                            callRefId={props.callRefId}
                                            completeId={props.completeId}
                                            question={q}
                                            readOnly={props.readOnly}
                                            grey={props.grey}
                                            updateParent={props.updateParent}
                                            answers={[]}
                                            key={q.id} />
                                    </Grid>
                                    :
                                    <Fragment key={q.id} />
                            )
                        }
                    </Fragment>
            }
        </Grid >
    );
}